import React from "react";
import * as Icons from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { FeatureCard } from "@/components/blocks/FeatureCard";
import { FAQ } from "@/components/blocks/FAQ";
import { CtaBand } from "@/components/blocks/CtaBand";
import { QuoteSection } from "@/components/blocks/QuoteSection";
import { TelephonyDashboard } from "@/components/visuals/TelephonyDashboard";
import { contactLink } from "@/data/site";

const FEATURES = [
  { icon: "Cloud", title: "Cloud Telephony", desc: "Make and receive business calls without traditional phone infrastructure." },
  { icon: "ListTree", title: "IVR", desc: "Create professional automated call menus that route callers efficiently." },
  { icon: "Shuffle", title: "Call Routing", desc: "Route calls to the right team, agent or department automatically." },
  { icon: "CircleDot", title: "Call Recording", desc: "Record business calls where legally permitted and appropriately disclosed." },
  { icon: "Activity", title: "Call Tracking", desc: "Track incoming and outgoing calls across your teams." },
  { icon: "Users", title: "Agent Management", desc: "Manage users, agents and calling activity from one place." },
  { icon: "BarChart3", title: "Call Analytics", desc: "Monitor call volumes, duration and performance metrics." },
  { icon: "Target", title: "Lead Management", desc: "Connect calling activity with lead management workflows." },
  { icon: "CalendarCheck", title: "Follow-Up", desc: "Help sales teams organise and track customer follow-ups." },
];

const FAQS = [
  { q: "What is a smart telephony system?", a: "It is a cloud-based business calling system that adds IVR, routing, recording, tracking and analytics on top of regular calling — helping sales and support teams work more effectively." },
  { q: "Do I need special hardware?", a: "No. Cloud telephony lets you make and receive business calls without traditional on-premise phone infrastructure." },
  { q: "Is call recording compliant?", a: "Call recording should be used only where legally permitted and with appropriate disclosure to callers, in line with applicable regulations." },
  { q: "Can it help with lead management?", a: "Yes. Calling activity can be connected with lead management and follow-up workflows so nothing slips through the cracks." },
];

export default function SmartTelephony() {
  useSEO({
    title: "Smart Telephony System & Cloud Telephony (IVR) | E Telecom",
    description: "Business calling made smarter with E Telecom's cloud telephony — IVR, call routing, recording, tracking, agent management, call analytics and lead management.",
    path: "/smart-telephony",
  });

  return (
    <>
      <PageHero
        eyebrow="Smart Telephony System"
        icon="PhoneCall"
        title="Turn Every Business Call Into a Smarter Conversation"
        description="Give your sales and support teams the tools to manage calls, leads and customer conversations from a single smart telephony system."
        primary={{ to: "/smart-telephony#quote", label: "Request Pricing" }}
        secondary={{ to: contactLink("Smart Telephony"), label: "Talk to Sales" }}
        visual={<TelephonyDashboard />}
      />

      <section className="et-section bg-white">
        <div className="et-container">
          <SectionHeading eyebrow="Capabilities" title="A complete cloud telephony toolkit" subtitle="Everything your teams need to handle business calls professionally — from IVR to analytics." />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => <FeatureCard key={f.title} icon={f.icon} title={f.title} description={f.desc} delay={(i % 3) * 0.05} />)}
          </div>
        </div>
      </section>

      <section className="et-section">
        <div className="et-container">
          <SectionHeading eyebrow="Live-style Overview" title="Your calling performance, visualised" subtitle="A clear view of calls, outcomes, agents and follow-ups. Figures shown are illustrative UI demonstrations." />
          <div className="mt-10 max-w-4xl mx-auto"><TelephonyDashboard /></div>
        </div>
      </section>

      <FAQ items={FAQS} title="Smart Telephony FAQs" />
      <QuoteSection service="Smart Telephony" title="Request Smart Telephony Pricing" subtitle="Tell us about your calling and team requirements — our team will share pricing tailored to your setup." testIdPrefix="telephony-quote" />
      <CtaBand title="Make every business call count" subtitle="Talk to our team about setting up smart telephony for your sales and support workflows." primary={{ to: "/smart-telephony#quote", label: "Request Pricing" }} secondary={{ to: contactLink("Smart Telephony"), label: "Talk to Sales" }} />
    </>
  );
}
